import { useMemo } from 'react';
import { ACMultiSelectDropdown, type ACMultiSelectOption } from './ACMultiSelectDropdown';
import './datatable.css';

export interface ACHierarchySelectorProps {
  redes: any[];
  estados: any[];
  municipios: any[];
  escolas: any[];

  selectedRedes: string[];
  onChangeRedes: (vals: string[]) => void;

  selectedEstados: string[];
  onChangeEstados: (vals: string[]) => void;

  selectedMunicipios: string[];
  onChangeMunicipios: (vals: string[]) => void;

  selectedEscolas: string[];
  onChangeEscolas: (vals: string[]) => void;
}

export function ACHierarchySelector({
  redes,
  estados,
  municipios,
  escolas,
  selectedRedes,
  onChangeRedes,
  selectedEstados,
  onChangeEstados,
  selectedMunicipios,
  onChangeMunicipios,
  selectedEscolas,
  onChangeEscolas,
}: ACHierarchySelectorProps) {

  // Coordinated filtering:
  // 1. Filter municipalities based on selected states
  const filteredMunicipios = useMemo(() => {
    if (selectedEstados.length === 0) return municipios;
    return municipios.filter((m) => selectedEstados.includes(m.estadoId));
  }, [municipios, selectedEstados]);

  // 2. Filter schools based on selected municipalities (or selected states if no municipality is chosen)
  const filteredEscolas = useMemo(() => {
    if (selectedMunicipios.length > 0) {
      return escolas.filter((esc) => selectedMunicipios.includes(esc.municipioId));
    }
    if (selectedEstados.length > 0) {
      const stateMunIds = municipios
        .filter((m) => selectedEstados.includes(m.estadoId))
        .map((m) => m.id);
      return escolas.filter((esc) => stateMunIds.includes(esc.municipioId));
    }
    return escolas;
  }, [escolas, selectedMunicipios, selectedEstados, municipios]);

  // Map database lists to dropdown options format
  const redesOptions: ACMultiSelectOption[] = useMemo(() => {
    return redes.map((r) => ({
      id: r.id,
      nome: r.nome,
      subtitulo: 'Rede Escolar',
    }));
  }, [redes]);

  const estadosOptions: ACMultiSelectOption[] = useMemo(() => {
    return estados.map((e) => ({
      id: e.id,
      nome: e.nome || e.uf,
      subtitulo: `UF: ${e.uf || ''}`,
    }));
  }, [estados]);

  const municipiosOptions: ACMultiSelectOption[] = useMemo(() => {
    return filteredMunicipios.map((m) => {
      const uf = estados.find((e) => e.id === m.estadoId)?.uf || '';
      return {
        id: m.id,
        nome: m.nome,
        subtitulo: uf ? `Estado: ${uf}` : 'Prefeitura Municipal',
      };
    });
  }, [filteredMunicipios, estados]);

  const escolasOptions: ACMultiSelectOption[] = useMemo(() => {
    return filteredEscolas.map((esc) => {
      const munName = municipios.find((m) => m.id === esc.municipioId)?.nome || '';
      return {
        id: esc.id,
        nome: esc.nome,
        subtitulo: munName ? `Cidade: ${munName}` : 'Unidade Escolar',
      };
    });
  }, [filteredEscolas, municipios]);

  return (
    <>
      {/* 2. Rede Filter (Multi Select) */}
      <div style={{ minWidth: 160 }}>
        <ACMultiSelectDropdown
          options={redesOptions}
          selectedIds={selectedRedes}
          onChange={onChangeRedes}
          placeholder="Todas as Redes"
          searchPlaceholder="Pesquisar rede..."
          showAvatars={false}
        />
      </div>

      {/* 3. Estado Filter (Multi Select) */}
      <div style={{ minWidth: 150 }}>
        <ACMultiSelectDropdown
          options={estadosOptions}
          selectedIds={selectedEstados}
          onChange={(next) => {
            onChangeEstados(next);
            // Dynamic cascading resets
            onChangeMunicipios([]);
            onChangeEscolas([]);
          }}
          placeholder="Todos os Estados"
          searchPlaceholder="Pesquisar estado..."
          showAvatars={false}
        />
      </div>

      {/* 4. Município Filter (Multi Select) */}
      <div style={{ minWidth: 160 }}>
        <ACMultiSelectDropdown
          options={municipiosOptions}
          selectedIds={selectedMunicipios}
          onChange={(next) => {
            onChangeMunicipios(next);
            onChangeEscolas([]);
          }}
          placeholder="Todos os Municípios"
          searchPlaceholder="Pesquisar município..."
          showAvatars={false}
        />
      </div>

      {/* 5. Escola Filter (Multi Select) */}
      <div style={{ minWidth: 180 }}>
        <ACMultiSelectDropdown
          options={escolasOptions}
          selectedIds={selectedEscolas}
          onChange={onChangeEscolas}
          placeholder="Todas as Escolas"
          searchPlaceholder="Pesquisar escola..."
          showAvatars={false}
        />
      </div>
    </>
  );
}
